<template>
    <div>
        <card-header icon="fa-fax">
            <template #title>
                <span v-if="fax.file">{{fax.file}}</span>
                <span v-else>Loading...</span>
            </template>
        </card-header>

        <card-toolbar v-if="fax.id">
            <!-- Browser PDF viewer already provides a download — re-enable if needed.
            <button @click="downloadPdf">
                <i class="far fa-download mr-1"></i>Download
            </button>
            <button @click="regenerateStudyLog" v-if="firstCEnrollId">
                <i class="far fa-redo mr-1"></i>Study Log
            </button>
            -->

            <button @click="$openCard('fax-reflections', {enrollId: firstCEnrollId}, card)" v-if="firstCEnrollId">
                <i class="far fa-comment-lines mr-1"></i>Reflections
            </button>
            <button @click="openEnrollment" v-if="firstCEnrollment">
                <i class="far fa-clipboard-list-check mr-1"></i>Enrollment
            </button>
            <button @click="openStudent" v-if="firstCEnrollment && firstCEnrollment.userID">
                <i class="far fa-user mr-1"></i>User
            </button>
        </card-toolbar>

        <card-body toolbar v-if="fax.id">
            <div class="fax-split">

                <!-- Left pane: details + form -->
                <div class="fax-split-left">

            <!-- Document details -->
            <card-list>
                <subheader-list-item title="Document" icon="fa-file-pdf"/>
                <container-list-item no-border>
                    <data-row title="ID">{{fax.id}}</data-row>
                    <data-row title="File">{{fax.file}}</data-row>
                    <data-row title="Received">{{formatDate(fax.timestamp)}}</data-row>
                    <data-row title="Processed">
                        <span v-if="fax.processed">{{formatDate(fax.processed)}}</span>
                        <span v-else class="has-text-grey">—</span>
                    </data-row>
                    <data-row title="Sort">
                        <span v-if="fax.sort">{{fax.sort | capitalize}}</span>
                        <span v-else class="has-text-grey">—</span>
                        <span v-if="fax.assigned_user">
                            &mdash;
                            <a class="link-card" @click="$openCard('user', {userId: fax.assigned_user.id}, card)">
                                {{fax.assigned_user.first_name}} {{fax.assigned_user.last_name}}
                            </a>
                        </span>
                    </data-row>
                    <data-row title="Source" v-if="fax.received_via">{{fax.received_via | capitalize}}</data-row>
                    <data-row title="Notes" v-if="fax.notes">{{fax.notes}}</data-row>
                </container-list-item>
            </card-list>

            <separator-list-item/>

            <!-- Linked enrollment summary -->
            <card-list v-if="firstCEnrollment">
                <subheader-list-item title="Linked Enrollment" icon="fa-link"/>
                <container-list-item no-border>
                    <data-row title="Course">
                        <a class="link-card" @click="openEnrollment">
                            {{firstCEnrollment.course_name || '—'}}
                            <i class="far fa-arrow-up-right-from-square ml-1"></i>
                        </a>
                    </data-row>
                    <data-row title="Custom Name" v-if="firstCEnrollment.customName">
                        <a class="link-card" @click="openEnrollment">{{firstCEnrollment.customName}}</a>
                    </data-row>
                    <data-row title="Student" v-if="firstCEnrollment.first_name">
                        <a class="link-card" @click="openStudent">
                            {{firstCEnrollment.first_name}} {{firstCEnrollment.last_name}}
                        </a>
                        <a v-if="firstCEnrollment.email" :href="`mailto:${firstCEnrollment.email}`" class="has-text-grey-light ml-2">
                            {{firstCEnrollment.email}}
                        </a>
                    </data-row>
                    <data-row title="Verified" v-if="firstCEnrollment.verifycode">
                        <code>{{firstCEnrollment.verifycode}}</code>
                        <span class="has-text-grey-light" v-if="firstCEnrollment.verifydate"> on {{formatDate(firstCEnrollment.verifydate)}}</span>
                    </data-row>
                </container-list-item>
            </card-list>

            <separator-list-item v-if="firstCEnrollment"/>

            <!-- STEM Award. Visible whenever the linked enrollment is flagged
                 stem=1 (legacy behavior). Total is editable to match the
                 legacy form, even though it isn't persisted on save. -->
            <card-list v-if="isStem">
                <subheader-list-item title="STEM Award" icon="fa-flask" badge="STEM"/>
                <container-list-item no-border>
                    <data-row title="STEM Hours">
                        <b-input v-model="stemUnits" type="number" step="0.01" size="is-small" expanded/>
                    </data-row>
                    <data-row title="Other Hours">
                        <b-input v-model="otherUnits" type="number" step="0.01" size="is-small" expanded/>
                    </data-row>
                    <data-row title="Total">
                        <b-input v-model="totalUnits" type="number" step="0.01" size="is-small" expanded/>
                    </data-row>
                    <div class="t-actions" v-if="fax.processed">
                        <b-button type="is-primary" size="is-small" icon-left="save" @click="saveStem">Save STEM Hours</b-button>
                    </div>
                </container-list-item>
            </card-list>

            <separator-list-item v-if="isStem"/>

            <!-- Process / Sort -->
            <card-list>
                <subheader-list-item title="Process" icon="fa-clipboard-check"/>
                <container-list-item no-border>
                    <data-row title="Sort">
                        <b-select v-model="form.sort" size="is-small" expanded>
                            <option value="code">Verification Code</option>
                            <option value="user">Specific User</option>
                            <option value="multi">Multiple Users</option>
                            <option value="credit">Credit Information</option>
                            <option value="other">Other Related</option>
                            <option value="unrelated">Unrelated</option>
                            <option value="future">Future</option>
                        </b-select>
                    </data-row>

                    <data-row title="Code(s)" v-if="form.sort === 'code'">
                        <b-input v-model="form.codes" placeholder="C123456" size="is-small" expanded/>
                    </data-row>

                    <data-row title="User" v-if="form.sort === 'user'">
                        <b-input v-model="userSearch" placeholder="Search users..." size="is-small" expanded @input="searchUser"/>
                    </data-row>

                    <div v-if="form.sort === 'user' && userResults.length" class="user-results">
                        <a v-for="u in userResults" :key="u.id" class="user-result" @click="selectUser(u)">
                            <strong>{{u.first_name}} {{u.last_name}}</strong>
                            <span class="has-text-grey-light"> &mdash; {{u.email}}</span>
                        </a>
                    </div>

                    <data-row title="Selected" v-if="form.sort === 'user' && selectedUser">
                        {{selectedUser.first_name}} {{selectedUser.last_name}}
                    </data-row>

                    <data-row title="Notes">
                        <b-input v-model="form.notes" type="textarea" size="is-small" rows="2" expanded/>
                    </data-row>

                    <data-row title="Error Email">
                        <b-checkbox v-model="form.sendError">Send processing-error notice to user</b-checkbox>
                    </data-row>

                    <data-row title="Message" v-if="form.sendError">
                        <b-input v-model="form.errorMessage" type="textarea" size="is-small" rows="5" expanded/>
                    </data-row>

                    <div class="t-actions">
                        <b-button type="is-primary" size="is-small" icon-left="check" :loading="submitting" @click="processFax">Process</b-button>
                        <span v-if="fax.sort" class="has-text-warning is-size-7 ml-2">
                            <i class="fas fa-info-circle"></i>
                            Already sorted as <strong>{{fax.sort}}</strong>{{fax.processed ? ' (processed)' : ''}}.
                        </span>
                    </div>
                </container-list-item>
            </card-list>

            <separator-list-item/>

            <!-- Linked enrollment uploads -->
            <card-list v-if="fax.linked_uploads && fax.linked_uploads.length">
                <subheader-list-item title="Enrollment Files" icon="fa-paperclip" :badge="fax.linked_uploads.length"/>
                <card-list-item v-for="u in fax.linked_uploads" :key="u.id" @click="openUpload(u)">
                    <template v-slot:icon>
                        <i class="far fa-file-arrow-down fa-fw"></i>
                    </template>
                    <template v-slot:content>
                        <div class="tile-title">{{u.title || u.name || u.file}}</div>
                        <div class="meta" v-if="u.name && u.title && u.name !== u.title">{{u.name}}</div>
                        <div class="meta" v-if="u.timestamp"><i class="far fa-clock fa-fw mr-1"></i>{{formatDate(u.timestamp)}}</div>
                    </template>
                    <template v-slot:status>
                        <i class="far fa-arrow-up-right-from-square has-text-grey-light"></i>
                    </template>
                </card-list-item>
            </card-list>

                </div>

                <!-- Right pane: PDF preview, sticky full-height -->
                <div class="fax-split-right">
                    <iframe v-if="previewUrl" :src="previewUrl" frameborder="0" class="pdf-iframe"></iframe>
                    <div v-else class="pdf-empty">
                        <i class="far fa-file-pdf fa-2x mb-2"></i>
                        <div class="has-text-grey is-size-7">No PDF available.</div>
                    </div>
                </div>

            </div>
        </card-body>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardToolbar from "@/TIER/components/CardToolbar";
    import CardList from "@/TIER/components/CardList";
    import CardListItem from "@/TIER/components/CardListItem";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import SeparatorListItem from "@/TIER/components/SeparatorListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import DataRow from "@/TIER/components/DataRow";
    import {client as http, axiosInstance, apiUrl} from "@/http_client";
    import moment from 'moment';

    export default {
        components: {
            CardHeader, CardBody, CardToolbar,
            CardList, CardListItem, SubheaderListItem, SeparatorListItem, ContainerListItem,
            DataRow
        },
        props: ['card', 'props', 'child'],
        filters: {
            capitalize(v) {
                return v ? String(v).charAt(0).toUpperCase() + String(v).slice(1) : '';
            }
        },
        data() {
            return {
                fax: {},
                stemSplit: null,
                stemUnits: 0,
                otherUnits: 0,
                totalUnits: 0,
                form: {
                    sort: 'code',
                    codes: '',
                    notes: '',
                    sendError: false,
                    errorMessage: this.defaultErrorMessage()
                },
                userSearch: '',
                userResults: [],
                selectedUser: null,
                submitting: false
            };
        },
        computed: {
            firstCEnrollment() {
                if (!this.fax.code_refs) return null;
                const ref = this.fax.code_refs.find(r => r.type === 'C' && r.enrollment);
                return ref ? ref.enrollment : null;
            },
            firstCEnrollId() {
                if (!this.fax.code_refs) return null;
                const ref = this.fax.code_refs.find(r => r.type === 'C');
                return ref ? ref.enrollId : null;
            },
            previewUrl() {
                return this.fax.preview_url || null;
            },
            // Show STEM section whenever the linked enrollment is flagged
            // stem=1, regardless of whether the studylog-based split lookup
            // succeeded. Mirrors the legacy `if (mysql_result(... 'stem') == '1')` gate.
            isStem() {
                return !!(this.firstCEnrollment && Number(this.firstCEnrollment.stem) === 1);
            }
        },
        mounted() {
            this.load();
        },
        methods: {
            defaultErrorMessage() {
                return "We've recently received a document by fax or mail, but there was a problem and we were unable to process it. Perhaps the document was unreadable, incomplete or inaccurate. The most common mistake is a missing, required signature or log entries being outside the enrollment period. If there are mistakes that need corrected do not make them on the form but rather contact us at support@my247ed.com.\n\nPlease review your document(s) and resubmit or contact us at your earliest convenience.\n\nIf you have any questions please don't hesitate to contact us: support@my247ed.com";
            },
            formatDate(d) {
                return d ? moment(d).format('MMM D, YYYY h:mm A') : '';
            },
            load() {
                http.get(`faxes/${this.props.faxId}`, {force: true}).then(response => {
                    this.fax = response.data;
                    this.form.codes = this.fax.codes || '';
                    this.form.notes = this.fax.notes || '';
                    if (this.fax.sort) this.form.sort = this.fax.sort;
                    this.stemSplit = this.fax.stem_split || null;

                    // Match legacy behavior: STEM and Other are recomputed from
                    // studylog on every load (NOT pulled from saved stem_units
                    // / other_units columns). Total mirrors enrollment.units.
                    // Saved values are only used as a fallback if the computed
                    // split lookup failed entirely.
                    const enroll = this.firstCEnrollment;
                    const split = this.stemSplit;
                    const pick = (...candidates) => {
                        for (const v of candidates) if (v != null && v !== '') return v;
                        return 0;
                    };
                    this.stemUnits  = pick(split && split.stem_units,  enroll && enroll.stem_units,  0);
                    this.otherUnits = pick(split && split.other_units, enroll && enroll.other_units, 0);
                    this.totalUnits = pick(enroll && enroll.units,     split && split.total_units,   0);
                }).catch(err => {
                    console.log('faxes.detail load failed', err);
                    const msg = (err.response && err.response.data && err.response.data.error) || 'Failed to load.';
                    this.$notify('error', 'Fax', msg);
                }).finally(() => {
                    this.$emit('loading', false);
                });
            },
            searchUser() {
                if (!this.userSearch || this.userSearch.length < 2) {
                    this.userResults = [];
                    return;
                }
                http.get(`users/search/${encodeURIComponent(this.userSearch)}/0/10`, {force: true}).then(response => {
                    const data = response.data;
                    this.userResults = Array.isArray(data) ? data : (data && data.users) || [];
                }).catch(() => {
                    this.userResults = [];
                });
            },
            selectUser(u) {
                this.selectedUser = u;
                this.userSearch = `${u.first_name} ${u.last_name}`;
                this.userResults = [];
            },
            processFax() {
                this.submitting = true;
                const body = {
                    sort: this.form.sort,
                    codes: this.form.codes,
                    notes: this.form.notes,
                    sendError: this.form.sendError,
                    errorMessage: this.form.errorMessage,
                    stemUnits: Number(this.stemUnits) || 0,
                    otherUnits: Number(this.otherUnits) || 0
                };
                if (this.form.sort === 'user' && this.selectedUser) {
                    body.userId = this.selectedUser.id;
                }
                http.post(`faxes/${this.fax.id}/process`, body).then(response => {
                    this.$notify('success', 'Fax', 'Processed.');
                    const failed = (response.data.verified || []).filter(v => v.error);
                    if (failed.length) {
                        this.$notify('warning', 'Fax', failed.map(f => `${f.code}: ${f.error}`).join('\n'));
                    }
                    this.load();
                }).catch(err => {
                    console.log(err);
                    const msg = (err.response && err.response.data && err.response.data.error) || 'Failed to process.';
                    this.$notify('error', 'Fax', msg);
                }).finally(() => {
                    this.submitting = false;
                });
            },
            saveStem() {
                if (!this.firstCEnrollId) return;
                http.patch(`faxes/enrollments/${this.firstCEnrollId}/stem`, {
                    stem_units: Number(this.stemUnits) || 0,
                    other_units: Number(this.otherUnits) || 0
                }).then(() => {
                    this.$notify('success', 'STEM Hours', 'Updated.');
                    this.load();
                }).catch(err => {
                    console.log(err);
                    this.$notify('error', 'STEM Hours', 'Failed to update.');
                });
            },
            authToken() {
                return (axiosInstance.defaults.headers.common['Authorization'] || '').replace('Bearer ', '');
            },
            openEnrollment() {
                if (!this.firstCEnrollment) return;
                // These are classic enrollments — there's no read-only info card,
                // just the edit-classic-enrollment one. It needs userId + enrollId.
                this.$openCard('edit-classic-enrollment', {
                    userId: this.firstCEnrollment.userID,
                    enrollId: this.firstCEnrollId
                }, this.card);
            },
            openStudent() {
                if (!this.firstCEnrollment || !this.firstCEnrollment.userID) return;
                this.$openCard('user', { userId: this.firstCEnrollment.userID }, this.card);
            },
            openUpload(upload) {
                if (upload && upload.download_url) {
                    window.open(upload.download_url, '_blank');
                }
            },
            downloadPdf() {
                if (this.fax.download_url) {
                    window.open(this.fax.download_url, '_blank');
                } else {
                    window.open(`${apiUrl}faxes/${this.fax.id}/file?auth=${this.authToken()}`, '_blank');
                }
            },
            regenerateStudyLog() {
                window.open(`${apiUrl}faxes/${this.fax.id}/study-log.pdf?auth=${this.authToken()}`, '_blank');
            }
        }
    };
</script>

<style lang="scss" scoped>
    .t-actions {
        padding: 0.6rem 1rem;
        border-top: 1px solid #efefef;
        display: flex;
        align-items: center;
    }

    .link-card {
        cursor: pointer;
        color: #336699;
        text-decoration: none;
        &:hover { text-decoration: underline; }
        i { font-size: 0.7rem; opacity: 0.6; }
    }

    .user-results {
        background: white;
        border: 1px solid #e7e7e7;
        border-radius: 4px;
        margin: 0 1rem 0.5rem;
        max-height: 220px;
        overflow-y: auto;
    }
    .user-result {
        display: block;
        padding: 6px 10px;
        font-size: 0.8rem;
        border-bottom: 1px solid #f0f0f0;
        color: inherit;
        cursor: pointer;
        &:last-child { border-bottom: 0; }
        &:hover { background: #fafafa; }
    }

    // Side-by-side layout: left pane scrolls (form + details), right pane
    // pins the PDF preview to the full card height. card-body is position:
    // relative + flex-grow:1, so the absolute child fills it cleanly without
    // double-scroll. Below ~960px we stack instead.
    .fax-split {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: stretch;
    }
    .fax-split-left {
        flex: 0 0 38%;
        min-width: 380px;
        max-width: 520px;
        overflow-y: auto;
        border-right: 1px solid #e7e7e7;
        background: #fafafa;
    }
    .fax-split-right {
        flex: 1;
        background: #2b2b2b;
        display: flex;
        align-items: stretch;
        justify-content: center;
        overflow: hidden;
    }
    .pdf-iframe {
        width: 100%;
        height: 100%;
        border: 0;
        display: block;
        background: #fff;
    }
    .pdf-empty {
        align-self: center;
        text-align: center;
        color: #aaa;
    }

    @media (max-width: 960px) {
        .fax-split {
            position: static;
            flex-direction: column;
        }
        .fax-split-left {
            flex: none;
            max-width: none;
            border-right: 0;
            border-bottom: 1px solid #e7e7e7;
        }
        .fax-split-right {
            flex: none;
            height: 600px;
        }
    }
</style>
